import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen,
  ScrollText,
  CalendarClock,
  Users,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  ShieldCheck,
  Sparkles,
  AlertCircle,
} from 'lucide-react';
import { linksApi } from '../services/api';

// ---- Relation/provenance UI metadata ----
const RELATION_LABEL = {
  quotes: 'Citation verbatim',
  references: 'Référence',
  explains: 'Explique',
  supports_interpretation: 'Appuie l\'interprétation',
  linked_by_tafsir: 'Relié via tafsir',
  event_context: 'Contexte historique',
  mentions: 'Mentionne',
  narrates_about: 'Narre à propos',
  related_to: 'En lien',
};

const PROVENANCE_LABEL = {
  editorial: 'Lien éditorial',
  imported: 'Source importée',
  rule_quote_detection: 'Détection de citation',
  derived_from_tafsir: 'Dérivé via tafsir',
  derived_from_event: 'Dérivé via ayah partagé',
  inferred: 'Inféré',
};

const CONFIDENCE_TONE = {
  high: 'bg-emerald-900/30 border-emerald-700/40 text-emerald-300',
  medium: 'bg-amber-900/30 border-amber-700/40 text-amber-300',
  low: 'bg-gray-800/40 border-gray-600/40 text-gray-400',
};

const TYPE_META = {
  ayah:   { label: 'Versets', icon: BookOpen,       color: 'text-primary' },
  hadith: { label: 'Hadiths', icon: ScrollText,     color: 'text-emerald-400' },
  event:  { label: 'Événements', icon: CalendarClock, color: 'text-amber-400' },
  prophet:{ label: 'Prophètes', icon: Users,        color: 'text-indigo-400' },
  companion: { label: 'Compagnons', icon: Users,    color: 'text-indigo-400' },
  tafsir_group: { label: 'Tafsir', icon: BookOpen,  color: 'text-cyan-400' },
  legacy_hadith: { label: 'Hadiths (édit.)', icon: ScrollText, color: 'text-emerald-400' },
};

// ---- Individual link card ----
const LinkCard = ({ link, resolved }) => {
  const tt = link.target_type;
  const meta = TYPE_META[tt] || TYPE_META.ayah;
  const Icon = meta.icon;
  const rez = resolved && resolved[link.target_id];
  const reviewed = link.reviewed;

  // Build routing & labels based on target_type
  let to = null;
  let primary = '';
  let secondary = null;
  let arabicPreview = null;

  if (tt === 'ayah') {
    const [s, a] = String(link.target_id).split(':');
    to = `/quran/${s}?ayah=${a}`;
    const surahName = rez?.surah?.name_french || rez?.surah?.name_english || '';
    primary = `Sourate ${s} — ${surahName}, verset ${a}`;
    arabicPreview = rez?.text_arabic;
    secondary = null;
  } else if (tt === 'hadith') {
    to = `/hadith/entry/${link.target_id}`;
    const ref = link.target_ref || {};
    const r = rez || {};
    primary = ref.collection_slug === 'bukhari'
      ? `Sahih al-Boukhari · Livre ${ref.book_number} · #${ref.id_in_book}`
      : ref.collection_slug === 'muslim'
        ? `Sahih Muslim · Livre ${ref.book_number} · #${ref.id_in_book}`
        : 'Hadith';
    arabicPreview = r.preview_ar;
    secondary = r.preview_en || ref.narrator_english;
  } else if (tt === 'event') {
    to = `/events/${link.target_id}`;
    const ref = link.target_ref || {};
    const r = rez || {};
    primary = r.title_fr || r.title_en || ref.title_fr || ref.title_en || 'Événement';
    secondary = (r.date_gregorian || ref.date_gregorian)
      ? `${r.date_gregorian || ref.date_gregorian} · ${r.period || ref.period || ''}`
      : null;
  } else if (tt === 'prophet') {
    to = `/prophets/${link.target_id}`;
    const ref = link.target_ref || {};
    const r = rez || {};
    primary = r.name_fr || r.name_en || ref.name || 'Prophète';
    secondary = r.era;
  } else {
    primary = `${meta.label} · ${link.target_id}`;
  }

  const confidenceTone = CONFIDENCE_TONE[link.confidence] || CONFIDENCE_TONE.low;

  const Content = (
    <div className="p-3 rounded-lg border border-border bg-surface-elevated/40 hover:border-primary/40 transition-all">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-md bg-black/30 flex items-center justify-center flex-shrink-0">
          <Icon className={`w-4 h-4 ${meta.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-1.5 mb-1">
            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${confidenceTone}`}>
              {RELATION_LABEL[link.relation_type] || link.relation_type}
            </span>
            {reviewed ? (
              <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400">
                <ShieldCheck className="w-3 h-3" /> Vérifié
              </span>
            ) : (
              <span
                className="inline-flex items-center gap-1 text-[10px] text-text-muted"
                title={PROVENANCE_LABEL[link.provenance] || link.provenance}
              >
                <Sparkles className="w-3 h-3" />
                {PROVENANCE_LABEL[link.provenance] || link.provenance}
              </span>
            )}
          </div>
          <p className="text-sm text-text-primary font-medium leading-snug">{primary}</p>
          {arabicPreview && (
            <p className="font-arabic text-base text-text-secondary text-right mt-2 line-clamp-2" dir="rtl">
              {arabicPreview}
            </p>
          )}
          {secondary && (
            <p className="text-xs text-text-muted mt-1 line-clamp-2">{secondary}</p>
          )}
          {link.evidence && link.confidence === 'low' && (
            <p className="text-[10px] text-text-muted mt-2 italic">{link.evidence}</p>
          )}
        </div>
        {to && <ExternalLink className="w-3.5 h-3.5 text-text-muted mt-1 flex-shrink-0" />}
      </div>
    </div>
  );

  return to ? (
    <Link to={to} data-testid={`related-link-${link.target_type}-${link.target_id}`}>
      {Content}
    </Link>
  ) : (
    <div data-testid={`related-link-${link.target_type}-${link.target_id}`}>{Content}</div>
  );
};

// ---- Section per target type ----
const LinkSection = ({ type, links, resolved, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  const meta = TYPE_META[type] || { label: type, icon: BookOpen, color: '' };
  const Icon = meta.icon;
  const visible = open ? links : [];

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-surface/60 hover:bg-surface transition-colors"
        data-testid={`related-section-toggle-${type}`}
      >
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${meta.color}`} />
          <span className="text-sm font-medium text-text-primary">{meta.label}</span>
          <span className="text-xs text-text-muted">({links.length})</span>
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 text-text-muted" />
        ) : (
          <ChevronDown className="w-4 h-4 text-text-muted" />
        )}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-3 space-y-2">
              {visible.map((lk) => (
                <LinkCard key={`${lk.target_type}-${lk.target_id}-${lk.relation_type}`}
                          link={lk} resolved={resolved[lk.target_type]} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ---- Main component ----
export const RelatedContent = ({ entityType, entityId, title = 'Contenu relié', types }) => {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState({});
  const [resolved, setResolved] = useState({});

  useEffect(() => {
    if (!entityType || !entityId) return;
    let cancelled = false;
    setLoading(true);
    setGroups({});
    setResolved({});
    linksApi
      .getLinks(entityType, entityId, { limit: 60 })
      .then(async (res) => {
        if (cancelled) return;
        const g = res.data.groups || {};
        // Apply optional type filter
        const filtered = types
          ? Object.fromEntries(Object.entries(g).filter(([k]) => types.includes(k)))
          : g;
        setGroups(filtered);

        // Resolve enrichment for each group
        const resolvedAll = {};
        await Promise.all(
          Object.entries(filtered).map(async ([tt, links]) => {
            if (!['ayah', 'hadith', 'event', 'prophet'].includes(tt)) return;
            const ids = links.map((l) => l.target_id).slice(0, 50);
            try {
              const r = await linksApi.resolve(tt, ids);
              resolvedAll[tt] = r.data.resolved || {};
            } catch (err) {
              console.error('resolve failed', tt, err);
              resolvedAll[tt] = {};
            }
          })
        );
        if (!cancelled) setResolved(resolvedAll);
      })
      .catch(console.error)
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [entityType, entityId, types && types.join(',')]);

  const entries = Object.entries(groups).filter(([, list]) => list && list.length > 0);

  if (loading) {
    return (
      <div className="text-sm text-text-muted">
        Chargement du contenu relié…
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="flex items-start gap-2 text-xs text-text-muted italic">
        <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <p>
          Aucun lien structuré à afficher pour cette entité pour l'instant.
          Les liens sémantiques s'enrichissent au fur et à mesure (éditoriaux,
          dérivés de tafsir, détection de citations).
        </p>
      </div>
    );
  }

  // Order: ayah, tafsir_group, hadith, event, prophet, companion
  const ORDER = ['ayah', 'tafsir_group', 'hadith', 'event', 'prophet', 'companion', 'legacy_hadith'];
  const ordered = entries.sort(([a], [b]) => ORDER.indexOf(a) - ORDER.indexOf(b));

  return (
    <div className="space-y-4" data-testid={`related-content-${entityType}`}>
      {title && (
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-text-primary uppercase tracking-wide">
            {title}
          </h3>
          <span className="text-[10px] text-text-muted">
            {entries.reduce((acc, [, v]) => acc + v.length, 0)} relations
          </span>
        </div>
      )}
      <div className="space-y-3">
        {ordered.map(([type, links]) => (
          <LinkSection key={type} type={type} links={links} resolved={resolved} />
        ))}
      </div>
    </div>
  );
};

export default RelatedContent;
